import { Check } from 'lucide-react';
import { useId, type InputHTMLAttributes } from 'react';

import { cn } from '@/lib/utils';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: string;
  hint?: string;
}

/** Checkbox con estilo propio (input nativo oculto para conservar accesibilidad). */
export function Checkbox({ label, hint, className, id, disabled, ...rest }: CheckboxProps) {
  const autoId = useId();
  const checkboxId = id ?? `check-${autoId}`;

  return (
    <div className="flex items-start gap-2.5">
      <span className="relative flex items-center">
        <input
          id={checkboxId}
          type="checkbox"
          disabled={disabled ?? false}
          className={cn(
            'peer size-4.5 cursor-pointer appearance-none rounded-[6px] border border-ink-300 bg-white transition-all',
            'checked:border-brand-500 checked:bg-brand-500 hover:border-brand-400',
            'disabled:cursor-not-allowed disabled:opacity-60',
            className,
          )}
          {...rest}
        />
        <Check
          aria-hidden="true"
          strokeWidth={3.5}
          className="pointer-events-none absolute left-1/2 top-1/2 size-3 -translate-x-1/2 -translate-y-1/2 text-white opacity-0 transition-opacity peer-checked:opacity-100"
        />
      </span>

      <label htmlFor={checkboxId} className="cursor-pointer select-none text-sm leading-tight">
        <span className="font-medium text-ink-700">{label}</span>
        {hint ? <span className="block text-xs text-ink-400">{hint}</span> : null}
      </label>
    </div>
  );
}
